import { BehaviorSubject, combineLatest, filter, of, switchMap, takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import { DesktopInventoryTableComponent } from '@inventory/components/desktop-inventory-table/desktop-inventory-table.component';
import { InventoryItemCreateOffcanvasComponent } from '@inventory/components/inventory-item-create-offcanvas/inventory-item-create-offcanvas.component';
import { InventoryItemDetailsOffcanvasComponent } from '@inventory/components/inventory-item-details-offcanvas/inventory-item-details-offcanvas.component';
import { InventoryItemStockManagementOffcanvasComponent } from '@inventory/components/inventory-item-stock-management-offcanvas/inventory-item-stock-management-offcanvas.component';
import { MobileInventoryTableComponent } from '@inventory/components/mobile-inventory-table/mobile-inventory-table.component';
import { IInventoryCategory, IInventoryItemWithCategory } from '@inventory/models/inventory';
import { InventoryCategoriesService } from '@inventory/services/inventory-categories/inventory-categories.service';
import { InventoryItemsService } from '@inventory/services/inventory-items/inventory-items.service';
import { LayoutService } from '@layout/services/layout/layout.service';
import { IOrganisationAndOrganisationUser, OrganisationUserAccessLevel } from '@organisations/models/organisation-user';
import { OrganisationSelectedService } from '@organisations/services/organisation-selected/organisation-selected.service';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { UnauthorisedBannerComponent } from '@shared/components/ui/unauthorised-banner/unauthorised-banner.component';
import { ToastTypes } from '@shared/models/toast';
import { OffcanvasService } from '@shared/services/offcanvas/offcanvas.service';
import { ToastService } from '@shared/services/toast/toast.service';

@Component({
	selector: 'app-inventory-items',
	imports: [
		LoadingStateComponent,
		CommonModule,
		UnauthorisedBannerComponent,
		MobileInventoryTableComponent,
		DesktopInventoryTableComponent,
	],
	templateUrl: './inventory-items.component.html',
	styleUrl: './inventory-items.component.scss',
})
export class InventoryItemsComponent implements OnInit, OnDestroy {
	destroyed$ = new EventEmitter<void>();
	private inventoryItemService = inject(InventoryItemsService);
	private inventoryCategoryService = inject(InventoryCategoriesService);
	private organisationSelectedService = inject(OrganisationSelectedService);
	private layoutService = inject(LayoutService);
	private offcanvasService = inject(OffcanvasService);
	private toastService = inject(ToastService);

	selectedOrganisation: IOrganisationAndOrganisationUser | null;

	isLoading = true;
	inventoryLoading = true;
	isMobileSize = true;
	userHasAccess = false;

	inventoryItems: Array<IInventoryItemWithCategory> = [];
	searchTerm$ = new BehaviorSubject<string>('');
	showArchived$ = new BehaviorSubject<boolean>(false);

	inventoryCategories: Array<IInventoryCategory> = [];

	ngOnInit(): void {
		this.layoutService.isMobileSize$.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
			this.isMobileSize = value;
		});

		combineLatest([this.organisationSelectedService.selectedOrganisation, this.searchTerm$, this.showArchived$])
			.pipe(
				takeUntil(this.destroyed$),
				filter(([selectedOrganisation]) => !!selectedOrganisation),
				switchMap(([selectedOrganisation, searchTerm, showArchived]) => {
					this.selectedOrganisation = selectedOrganisation;
					if (
						selectedOrganisation &&
						[OrganisationUserAccessLevel.OWNER, OrganisationUserAccessLevel.ADMIN].includes(
							selectedOrganisation.organisationUser.accessLevel,
						)
					) {
						this.userHasAccess = true;
						this.inventoryLoading = true;

						return combineLatest([
							this.inventoryItemService.getInventoryItemsWithCategoryByOrganisation(
								selectedOrganisation.organisationUser.organisation,
								searchTerm,
								showArchived,
							),
							this.inventoryCategoryService.getInventoryCategoriesByOrganisationAll(
								selectedOrganisation.organisationUser.organisation,
							),
						]);
					} else {
						this.userHasAccess = false;
						this.isLoading = false;
						return of([[], []]);
					}
				}),
			)
			.subscribe(([inventoryItems, inventoryCategories]) => {
				this.inventoryItems = inventoryItems;
				this.inventoryCategories = inventoryCategories;
				this.isLoading = false;
				this.inventoryLoading = false;
			});
	}

	ngOnDestroy(): void {
		this.destroyed$.emit();
	}

	onInventoryItemEdit(inventoryItem: IInventoryItemWithCategory) {
		const ref = this.offcanvasService.open(InventoryItemDetailsOffcanvasComponent, 'end', true, 'oc-lg');
		const componentInstance = ref.componentInstance as InventoryItemDetailsOffcanvasComponent;
		componentInstance.inventoryItem = inventoryItem;
		componentInstance.categories = this.inventoryCategories.map((category) => ({
			label: category.name,
			value: category.id,
			id: category.id as string,
			disabled: category.archived,
		}));
	}

	onInventoryItemManage(inventoryItem: IInventoryItemWithCategory) {
		const ref = this.offcanvasService.open(InventoryItemStockManagementOffcanvasComponent, 'end', true, 'oc-xl');
		const componentInstance = ref.componentInstance as InventoryItemStockManagementOffcanvasComponent;
		componentInstance.inventoryItem = inventoryItem;
	}

	onInventoryItemArchive(inventoryItem: IInventoryItemWithCategory) {
		this.inventoryItemService
			.archiveInventoryItem(inventoryItem.id as string)
			.then(() => {
				this.toastService.showToast({
					title: 'Inventory Item',
					message: `Successfully archived inventory item.`,
					type: ToastTypes.SUCCESS,
				});
			})
			.catch((error) => {
				console.error('Error archiving inventory item:', error);
				this.toastService.showToast({
					title: 'Inventory Item',
					message: `Failed to archive inventory item: ${error.message}`,
					type: ToastTypes.ERROR,
				});
			});
	}

	onInventoryItemUnarchive(inventoryItem: IInventoryItemWithCategory) {
		this.inventoryItemService
			.unArchiveInventoryItem(inventoryItem.id as string)
			.then(() => {
				this.toastService.showToast({
					title: 'Inventory Item',
					message: `Successfully unarchived inventory item.`,
					type: ToastTypes.SUCCESS,
				});
			})
			.catch((error) => {
				console.error('Error unarchiving inventory item:', error);
				this.toastService.showToast({
					title: 'Inventory Item',
					message: `Failed to unarchive inventory item: ${error.message}`,
					type: ToastTypes.ERROR,
				});
			});
	}

	openAddInventoryItemOffcanvas() {
		const ref = this.offcanvasService.open(InventoryItemCreateOffcanvasComponent, 'end', true, 'oc-lg');
		const componentInstance = ref.componentInstance as InventoryItemCreateOffcanvasComponent;
		componentInstance.organisationRef = this.selectedOrganisation!.organisationUser.organisation;
		componentInstance.categories = this.inventoryCategories
			.filter((category) => !category.archived)
			.map((category) => ({
				label: category.name,
				value: category.id,
				id: category.id as string,
				disabled: category.archived,
			}));
	}
}
